import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
    /**
     * Specify your server-side environment variables schema here. This way you can ensure the app
     * isn't built with invalid env vars.
     */
    server: {
        // Database URLs
        APP_DATABASE_URL: z.url(),

        // NextAuth Configuration
        NEXTAUTH_URL: z.url(),
        NEXT_AUTH_SECRET: z.string().min(1),

        // OAuth Service (modules/oauth) - issuer origin advertised in discovery
        // metadata. Optional; falls back to NEXTAUTH_URL when unset.
        OAUTH_ISSUER_URL: z.url().optional(),

        // OAuth Provider - Google
        GOOGLE_CLIENT_ID: z.string().min(1),
        GOOGLE_CLIENT_SECRET: z.string().min(1),

        // OAuth Provider -LinkedIn
        // LINKEDIN_CLIENT_ID: z.string().optional(),
        // LINKEDIN_CLIENT_SECRET: z.string().optional(),

        // OAuth Provider - Microsoft
        // Azure AD B2C
        // AZURE_AD_B2C_TENANT_NAME: z.string().min(1),
        // AZURE_AD_B2C_CLIENT_ID: z.string().min(1),
        // AZURE_AD_B2C_CLIENT_SECRET: z.string().min(1),
        // AZURE_AD_B2C_PRIMARY_USER_FLOW: z.string().min(1),

        // Email Configuration (Resend)
        RESEND_EMAIL_SERVER_USER: z.string().min(1),
        RESEND_EMAIL_SERVER_PASSWORD: z.string().min(1),
        RESEND_EMAIL_SERVER_HOST: z.string().min(1),
        RESEND_EMAIL_SERVER_PORT: z.coerce.number().int().positive(),
        RESEND_EMAIL_FROM: z.string().min(1),
        RESEND_ONBOARDING_EMAIL_FROM: z.string().min(1),

        // LaunchDarkly
        LAUNCH_DARKLY_SERVER_SDK_KEY: z.string().min(1),

        // RWA API
        RWA_API_HOST: z.url(),
        RWA_API_S2S_TOKEN: z.string().min(1),

        // Vercel AI Gateway (asset document chat). Optional so the app still boots
        // without it; Vercel deployments can also authenticate with OIDC.
        AI_GATEWAY_API_KEY: z.string().min(1).optional(),

        // Jupiter Swap API (portal.jup.ag). Optional so the app still boots
        // without it; the swap API routes return 503 when unset.
        JUPITER_API_KEY: z.string().min(1).optional(),

        // Slack Integration
        RWAXYZ_SLACK_BOT_OAUTH_TOKEN: z.string().optional(),

        // Redis
        UPSTASH_REDIS_REST_URL: z.url(),
        UPSTASH_REDIS_REST_TOKEN: z.string().min(1),

        // Intercom
        INTERCOM_HMAC_SECRET: z.string().optional(),

        // Attio CRM
        // ATTIO_API_KEY: z.string().optional(),

        // Stripe
        STRIPE_SECRET_KEY: z.string().optional(),
        STRIPE_PRODUCT_PRICE_DOWNLOADS: z.string(),
        STRIPE_PRODUCT_PRICE_DOWNLOADS_50_MONTHLY: z.string(),
        STRIPE_PRODUCT_PRICE_ACADEMIC_MONTHLY: z.string(),
        STRIPE_WEBHOOK_SECRET: z.string().optional(),

        // Development/Testing
        DANGEROUS_IGNORE_USER_AUTH: z.string().optional(),

        // Hosting Platform Variables
        VERCEL_URL: z.string().optional(),
        RENDER_INTERNAL_HOSTNAME: z.string().optional(),
        PORT: z.coerce.number().int().positive().optional(),

        NODE_ENV: z
            .enum(["development", "test", "production"])
            .default("development"),
    },

    /**
     * Specify your client-side environment variables schema here. This way you can ensure the app
     * isn't built with invalid env vars. To expose them to the client, prefix them with
     * `NEXT_PUBLIC_`.
     */
    client: {
        // Analytics
        NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID: z.string().min(1).optional(),
        NEXT_PUBLIC_INTERCOM_APP_ID: z.string().min(1).optional(),

        // PostHog Analytics
        NEXT_PUBLIC_POSTHOG_KEY: z.string().min(1).optional(),
        NEXT_PUBLIC_POSTHOG_HOST: z.url().optional(),

        // Snitcher Analytics
        NEXT_PUBLIC_SNITCHER_PROFILE_ID: z.string().min(1).optional(),

        // Base URL
        NEXT_PUBLIC_BASE_URL: z.url(),

        // Development Overrides
        NEXT_PUBLIC_DEV_MAX_INTERNAL_PAGE_VISITS_WITHOUT_LOGIN: z.coerce
            .number()
            .int()
            .positive()
            .optional(),
    },

    /**
     * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
     * middlewares) or client-side so we need to destruct manually.
     */
    runtimeEnv: {
        // Server
        APP_DATABASE_URL: process.env.APP_DATABASE_URL,
        NEXTAUTH_URL: process.env.NEXTAUTH_URL,
        NEXT_AUTH_SECRET: process.env.NEXT_AUTH_SECRET,
        OAUTH_ISSUER_URL: process.env.OAUTH_ISSUER_URL,
        GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID,
        GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET,
        // LINKEDIN_CLIENT_ID: process.env.LINKEDIN_CLIENT_ID,
        // LINKEDIN_CLIENT_SECRET: process.env.LINKEDIN_CLIENT_SECRET,
        // AZURE_AD_B2C_TENANT_NAME: process.env.AZURE_AD_B2C_TENANT_NAME,
        // AZURE_AD_B2C_CLIENT_ID: process.env.AZURE_AD_B2C_CLIENT_ID,
        // AZURE_AD_B2C_CLIENT_SECRET: process.env.AZURE_AD_B2C_CLIENT_SECRET,
        // AZURE_AD_B2C_PRIMARY_USER_FLOW: process.env.AZURE_AD_B2C_PRIMARY_USER_FLOW,
        RESEND_EMAIL_SERVER_USER: process.env.RESEND_EMAIL_SERVER_USER,
        RESEND_EMAIL_SERVER_PASSWORD: process.env.RESEND_EMAIL_SERVER_PASSWORD,
        RESEND_EMAIL_SERVER_HOST: process.env.RESEND_EMAIL_SERVER_HOST,
        RESEND_EMAIL_SERVER_PORT: process.env.RESEND_EMAIL_SERVER_PORT,
        RESEND_EMAIL_FROM: process.env.RESEND_EMAIL_FROM,
        RESEND_ONBOARDING_EMAIL_FROM: process.env.RESEND_ONBOARDING_EMAIL_FROM,
        LAUNCH_DARKLY_SERVER_SDK_KEY: process.env.LAUNCH_DARKLY_SERVER_SDK_KEY,
        RWA_API_HOST: process.env.RWA_API_HOST,
        RWA_API_S2S_TOKEN: process.env.RWA_API_S2S_TOKEN,
        AI_GATEWAY_API_KEY: process.env.AI_GATEWAY_API_KEY,
        JUPITER_API_KEY: process.env.JUPITER_API_KEY,
        RWAXYZ_SLACK_BOT_OAUTH_TOKEN: process.env.RWAXYZ_SLACK_BOT_OAUTH_TOKEN,
        UPSTASH_REDIS_REST_URL: process.env.UPSTASH_REDIS_REST_URL,
        UPSTASH_REDIS_REST_TOKEN: process.env.UPSTASH_REDIS_REST_TOKEN,
        INTERCOM_HMAC_SECRET: process.env.INTERCOM_HMAC_SECRET,
        STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
        STRIPE_PRODUCT_PRICE_DOWNLOADS:
            process.env.STRIPE_PRODUCT_PRICE_DOWNLOADS,
        STRIPE_PRODUCT_PRICE_DOWNLOADS_50_MONTHLY:
            process.env.STRIPE_PRODUCT_PRICE_DOWNLOADS_50_MONTHLY,
        STRIPE_PRODUCT_PRICE_ACADEMIC_MONTHLY:
            process.env.STRIPE_PRODUCT_PRICE_ACADEMIC_MONTHLY,
        STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,
        DANGEROUS_IGNORE_USER_AUTH: process.env.DANGEROUS_IGNORE_USER_AUTH,
        VERCEL_URL: process.env.VERCEL_URL,
        RENDER_INTERNAL_HOSTNAME: process.env.RENDER_INTERNAL_HOSTNAME,
        PORT: process.env.PORT,
        NODE_ENV: process.env.NODE_ENV,

        // Client
        NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID:
            process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID,
        NEXT_PUBLIC_INTERCOM_APP_ID: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
        NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
        NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
        NEXT_PUBLIC_SNITCHER_PROFILE_ID:
            process.env.NEXT_PUBLIC_SNITCHER_PROFILE_ID,
        NEXT_PUBLIC_BASE_URL: process.env.NEXT_PUBLIC_BASE_URL,
        NEXT_PUBLIC_DEV_MAX_INTERNAL_PAGE_VISITS_WITHOUT_LOGIN:
            process.env.NEXT_PUBLIC_DEV_MAX_INTERNAL_PAGE_VISITS_WITHOUT_LOGIN,
    },

    /**
     * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially
     * useful for Docker builds.
     */
    skipValidation: !!process.env.SKIP_ENV_VALIDATION,

    /**
     * Makes it so that empty strings are treated as undefined. `SOME_VAR: z.string()` and
     * `SOME_VAR=''` will throw an error.
     */
    emptyStringAsUndefined: true,
});
